// src/pages/public/CheckoutSuccess.jsx
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import QrCode2Icon from "@mui/icons-material/QrCode2";

function fmt(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function qrUrl(value) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(value)}`;
}

export default function CheckoutSuccess() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // se o usuário acessar diretamente sem state, redireciona
  if (!state?.orderId) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
          Nenhum pedido encontrado
        </Typography>
        <Button variant="contained" onClick={() => navigate("/meus-ingressos")}>
          Meus ingressos
        </Button>
      </Container>
    );
  }

  const { orderId, tickets = [], cart, buyer } = state;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      {/* Header de confirmação */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <CheckCircleIcon
          sx={{ fontSize: 64, color: "var(--verdeClaro)", mb: 1.5 }}
        />
        <Typography
          variant="h3"
          sx={{ fontWeight: 950, letterSpacing: -0.8, mb: 1 }}
        >
          Compra confirmada!
        </Typography>
        <Typography sx={{ opacity: 0.7, maxWidth: "55ch", mx: "auto" }}>
          Seus ingressos foram gerados e já estão disponíveis em{" "}
          <strong>Meus Ingressos</strong>. Guarde os QR Codes abaixo para o
          check-in.
        </Typography>
        <Chip
          label={`Pedido: ${orderId}`}
          sx={{
            mt: 2,
            fontWeight: 900,
            fontFamily: "monospace",
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.05)",
          }}
        />
      </Box>

      {/* Resumo do pedido */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 950, fontSize: 18 }}>
              {cart?.event?.title}
            </Typography>
            <Typography sx={{ opacity: 0.65, fontSize: 13 }}>
              {cart?.event?.location?.city} · {cart?.event?.location?.state}
            </Typography>
          </Box>
          <Chip
            label="Pago"
            sx={{
              fontWeight: 950,
              border: "1px solid rgba(26,255,122,0.34)",
              background: "rgba(26,255,122,0.12)",
              color: "var(--verdeClaro)",
            }}
          />
        </Stack>

        <Divider sx={{ mb: 2, opacity: 0.2 }} />

        <Stack spacing={0.8}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ opacity: 0.75 }}>Comprador</Typography>
            <Typography sx={{ fontWeight: 800 }}>{buyer?.name}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ opacity: 0.75 }}>E-mail</Typography>
            <Typography sx={{ fontWeight: 800 }}>{buyer?.email}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ opacity: 0.75 }}>Ingressos</Typography>
            <Typography sx={{ fontWeight: 800 }}>{tickets.length}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ opacity: 0.75 }}>Total pago</Typography>
            <Typography sx={{ fontWeight: 950 }}>
              {fmt(cart?.summary?.total)}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Ingressos com QR */}
      <Typography
        sx={{ fontWeight: 950, fontSize: 18, mb: 2, display: "flex", alignItems: "center", gap: 1 }}
      >
        <ConfirmationNumberIcon />
        Seus ingressos ({tickets.length})
      </Typography>

      <Stack spacing={2} sx={{ mb: 4 }}>
        {tickets.map((ticket, idx) => (
          <Paper
            key={ticket.ticketId}
            sx={{
              p: 0,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
              }}
            >
              {/* Info do ingresso */}
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label="Válido"
                    sx={{
                      fontWeight: 950,
                      border: "1px solid rgba(26,255,122,0.34)",
                      background: "rgba(26,255,122,0.12)",
                      color: "var(--verdeClaro)",
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Ingresso ${idx + 1} de ${tickets.length}`}
                    sx={{
                      fontWeight: 900,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.05)",
                    }}
                  />
                </Stack>

                <Typography sx={{ fontWeight: 950, fontSize: 18, mb: 0.3 }}>
                  {ticket.ticketTypeName}
                </Typography>
                <Typography sx={{ opacity: 0.7, fontSize: 13, mb: 1 }}>
                  {ticket.lotName}
                </Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  {fmt(ticket.totalPaid)}
                </Typography>

                <Divider sx={{ my: 1.5, opacity: 0.2 }} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <QrCode2Icon sx={{ opacity: 0.5, fontSize: 18 }} />
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      opacity: 0.6,
                      wordBreak: "break-all",
                    }}
                  >
                    {ticket.qrValue}
                  </Typography>
                </Stack>
              </Box>

              {/* QR Code */}
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  borderLeft: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <Box
                  component="img"
                  src={qrUrl(ticket.qrValue)}
                  alt={`QR Code ingresso ${idx + 1}`}
                  sx={{
                    width: 130,
                    height: 130,
                    borderRadius: 1.5,
                    background: "#fff",
                    p: 1,
                  }}
                />
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Ações */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/meus-ingressos")}
          sx={{
            px: 4,
            py: 1.4,
            borderRadius: 2,
            fontWeight: 950,
            background: "linear-gradient(-135deg, var(--verdeClaro), #6dffb9)",
            color: "#1e1e1e",
          }}
        >
          Ver meus ingressos
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate("/eventos")}
          sx={{ px: 4, py: 1.4, borderRadius: 2, fontWeight: 900 }}
        >
          Explorar mais eventos
        </Button>
      </Stack>
    </Container>
  );
}
